import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { images, certifications } from '../../data/mock';


// Use lucide-react icons (already used elsewhere in your project)
import { Award, Shield, Globe,BadgeCheck, Rocket, Target, ArrowRight } from 'lucide-react';

const TrackRecordPage = () => {
  const { language, t, isRTL } = useLanguage();


  return (
    <div className="min-h-screen bg-slate-950 text-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section, Trust Indicators, and Syria Qualification */}
      <section
        className="py-32 bg-gradient-to-b from-zinc-950 to-black bg-cover bg-center"
        style={{ backgroundImage: "url('/images/belarus_syria_BG.png')" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="max-w-4xl mb-24">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('trackRecord.pageTitle')}
            </h1>
          </div>

          {/* Trust Indicators */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-8">
                <Award className="h-5 w-5 text-amber-500" />
                <span className="text-amber-400 font-medium">
                  {t('trackRecord.endorsementLabel')}
                </span>
              </div>

              <h2 className="text-3xl font-bold text-white mb-6">
                {t('trackRecord.belarusTitle')}
              </h2>

              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {t('trackRecord.belarusDesc')}
              </p>

              <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl flex items-center gap-4">
                <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="h-8 w-8 text-amber-500" />
                </div>
                <div>
                  <div className="text-white font-semibold">{t('trackRecord.repBelarus')}</div>
                  <div className="text-gray-400 text-sm">{t('trackRecord.belarusShort')}</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src={images.refinery}
                alt={t('trackRecord.refineryImageAlt')}
                className="w-full rounded-2xl shadow-2xl shadow-black/50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl" />
            </div>
          </div>

          {/* Syria Qualification */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={images.drilling}
                alt={t('trackRecord.drillingImageAlt')}
                className="w-full rounded-2xl shadow-2xl shadow-black/50"
              />
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8">
                <Shield className="h-5 w-5 text-emerald-500" />
                <span className="text-emerald-400 font-medium">
                  {t('trackRecord.internationalQualificationLabel')}
                </span>
              </div>

              <h2 className="text-3xl font-bold text-white mb-6">
                {t('trackRecord.syriaTitle')}
              </h2>

              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {t('trackRecord.syriaDesc')}
              </p>

              <div className="p-6 bg-gradient-to-r from-emerald-950/30 to-zinc-900/50 border border-emerald-900/30 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BadgeCheck className="h-8 w-8 text-emerald-500" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{t('trackRecord.syriaBadge')}</div>
                    <div className="text-gray-400 text-sm">{t('trackRecord.syriaQualificationDate')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accuracy Section */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Target className="h-12 w-12 text-red-500" />
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              {t('trackRecord.accuracyTitle')}
            </h2>

            <p className="text-gray-400 text-lg leading-relaxed mb-12">
              {t('trackRecord.accuracyDesc')}
            </p>

            <div className="inline-flex items-center justify-center gap-4 px-8 py-6 bg-gradient-to-r from-red-950/50 to-zinc-900/50 border border-red-900/30 rounded-2xl">
              <div className="text-6xl font-bold text-red-500">{t('trackRecord.successRatePercent')}</div>
              <div className="text-left">
                <div className="text-white font-semibold">{t('trackRecord.successRateLabel')}</div>
                <div className="text-gray-400">{t('trackRecord.dryTrapDetectionLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies and Vision & Mission */}
      <section
        className="py-24 bg-slate-950 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/Case_Studies_vision_BG.png')", width: "100%" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {t('trackRecord.caseStudiesTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Case Study 1 */}
            <div className="bg-zinc-900/60 p-6 rounded-lg border border-zinc-800/70 shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-emerald-300">
                {t('trackRecord.caseStudyKrasnodarTitle')}
              </h3>
              <p className="text-gray-300">
                {t('trackRecord.caseStudyKrasnodarBody')}
              </p>
            </div>
            {/* Case Study 2 */}
            <div className="bg-zinc-900/60 p-6 rounded-lg border border-zinc-800/70 shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-blue-300">
                {t('trackRecord.caseStudySamaraTitle')}
              </h3>
              <p className="text-gray-300">
                {t('trackRecord.caseStudySamaraBody')}
              </p>
            </div>
            {/* Case Study 3 */}
            <div className="bg-zinc-900/60 p-6 rounded-lg border border-zinc-800/70 shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-purple-300">
                {t('trackRecord.caseStudyKazakhstanTitle')}
              </h3>
              <p className="text-gray-300">
                {t('trackRecord.caseStudyKazakhstanBody')}
              </p>
            </div>
          </div>
        </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-center my-12">
            {t('trackRecord.visionmainTitle')}
          </h2>
          
        <div className="max-w-6xl mx-auto ">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-center items-stretch justify-items-center md:gap-12">
            {/* Vision Card */}
            
            <div className="relative p-6 bg-zinc-900/60 border border-zinc-800 rounded-2xl transition-all card-hover flex flex-col md:flex-grow">
              <div className="absolute inset-0 card-shimmer rounded-2xl" aria-hidden="true" />
              <div className="flex flex-col items-center gap-4 card-content">
                <div className="w-16 h-16 bg-[rgba(220,38,38,0.06)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="h-10 w-10 text-[rgb(150,20,20)] icon-pulse" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('trackRecord.visionTitle')}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{t('trackRecord.vision')}</p>
                </div>
              </div>
            </div>

            {/* Mission Card */}
            <div className="relative p-6 bg-zinc-900/60 border border-zinc-800 rounded-2xl transition-all card-hover flex flex-col md:flex-grow">
              <div className="absolute inset-0 card-shimmer rounded-2xl" aria-hidden="true" />
              <div className="flex flex-col items-center gap-4 card-content">
                <div className="w-16 h-16 bg-[rgba(220,38,38,0.06)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Rocket className="h-10 w-10 text-[rgb(150,20,20)] icon-pulse" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('trackRecord.missionTitle')}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{t('trackRecord.mission')}</p>
                </div>
              </div>
            </div>

            {/* Objective Card */}
            <div className="relative p-6 bg-zinc-900/60 border border-zinc-800 rounded-2xl transition-all card-hover flex flex-col md:flex-grow">
              <div className="absolute inset-0 card-shimmer rounded-2xl" aria-hidden="true" />
              <div className="flex flex-col items-center gap-4 card-content">
                <div className="w-16 h-16 bg-[rgba(220,38,38,0.06)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="h-10 w-10 text-[rgb(150,20,20)] icon-pulse" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('trackRecord.objectiveTitle')}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{t('trackRecord.objective')}</p>
                </div>
              </div>
            </div>
            <div className="mt-9 flex justify-center" aria-hidden="true">
            <div className="w-full max-w-3xl fading-underline" style={{ height: '4px', opacity: 1 }} />
          </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            {t('cta.title')}
          </h2>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
            >
              {t('cta.button')}
              <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </Link>
        </div>
      </section> */}
    </div>
  );
};

export default TrackRecordPage;
